import React, { useEffect, useState, useCallback } from "react";
import { usePageTitle } from "../../../hooks/useMeta";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  getCompanyPartners,
  getCompanyDomains,
  addCompanyDomain,
} from "../../../services/companyService";
import { getCompanyUsers } from "../../../services/userService";
import Tag from "../../utils/Tag";
import Button from "../../buttons/Button";
import Modal from "../../utils/Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightLong } from "@fortawesome/pro-light-svg-icons";
import { faClipboard } from "@fortawesome/pro-regular-svg-icons";
import { copyToClipboard } from "../../../utils/download";
import { extractDomain, isGenericDomain } from "../../../utils/emailDomains";
import useLangNavigate from "../../../hooks/useLangNavigate";
import useApi from "../../../hooks/useApi";
import Spinner from "../../utils/Spinner";

export const CompanyOverview = ({ tKey = "dashboard.company." }) => {
  usePageTitle("companyOverview");
  const { t } = useTranslation();
  const navigate = useLangNavigate();
  const [partners, setPartners] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [pendingEmployees, setPendingEmployees] = useState([]);
  const [validationCode, setValidationCode] = useState("");
  const [domainPromptOpen, setDomainPromptOpen] = useState(false);
  const [suggestedDomain, setSuggestedDomain] = useState("");
  const companyId = useSelector((state) => state.user.companyId);
  const userEmail = useSelector((state) => state.user.email);
  const { request: getCompanyPartnersRequest, loading: partnersLoading } =
    useApi(getCompanyPartners);
  const { request: getCompanyUsersRequest, loading: usersLoading } =
    useApi(getCompanyUsers);
  const { request: getCompanyDomainsRequest } = useApi(getCompanyDomains);
  const { request: addCompanyDomainRequest } = useApi(addCompanyDomain);

  const fetchPartners = useCallback(
    async (companyId) => {
      await getCompanyPartnersRequest(companyId)
        .then((response) => {
          setPartners(response.data);
        })
        .catch((error) => {
          console.log(error);
        });
    },
    [getCompanyPartnersRequest]
  );

  const fetchEmployees = useCallback(
    async (companyId) => {
      await getCompanyUsersRequest(companyId)
        .then((response) => {
          setValidationCode(response.data.validationCode);
          setEmployees(
            response.data.users.filter((u) => u.usercompany.isConfirmed)
          );
          setPendingEmployees(
            response.data.users.filter((u) => !u.usercompany.isConfirmed)
          );
        })
        .catch((error) => {
          console.log(error);
        });
    },
    [getCompanyUsersRequest]
  );

  const promptStorageKey = useCallback(
    () => `offood_company_domain_prompt_${companyId}`,
    [companyId]
  );

  const dismissDomainPrompt = useCallback(() => {
    localStorage.setItem(promptStorageKey(), "1");
    setDomainPromptOpen(false);
  }, [promptStorageKey]);

  const confirmDomainPrompt = useCallback(async () => {
    try {
      await addCompanyDomainRequest(companyId, suggestedDomain);
      toast.success(
        t(`${tKey}domainPrompt.success`, { domain: suggestedDomain })
      );
    } catch (error) {
      console.error(error);
      toast.error(error.message);
    } finally {
      dismissDomainPrompt();
    }
  }, [
    addCompanyDomainRequest,
    companyId,
    suggestedDomain,
    t,
    tKey,
    dismissDomainPrompt,
  ]);

  // First-login suggestion: propose adding the admin's own email domain
  const checkDomainPrompt = useCallback(async () => {
    if (!companyId || !userEmail) return;
    if (localStorage.getItem(promptStorageKey())) return;
    const domain = extractDomain(userEmail);
    if (!domain || isGenericDomain(domain)) {
      localStorage.setItem(promptStorageKey(), "1");
      return;
    }
    try {
      const res = await getCompanyDomainsRequest(companyId);
      if ((res.data || []).length === 0) {
        setSuggestedDomain(domain);
        setDomainPromptOpen(true);
      } else {
        localStorage.setItem(promptStorageKey(), "1");
      }
    } catch (error) {
      console.error(error);
    }
  }, [companyId, userEmail, promptStorageKey, getCompanyDomainsRequest]);

  useEffect(() => {
    fetchPartners(companyId);
    fetchEmployees(companyId);
  }, [companyId, fetchEmployees, fetchPartners]);

  useEffect(() => {
    checkDomainPrompt();
  }, [checkDomainPrompt]);

  if (partnersLoading || usersLoading) return <Spinner />;

  return (
    <>
    <div
      id="company-overview"
      className="dashboard-content flex flex-wrap gap-2"
    >
      <div className="bg-white flex-grow rounded p-3 flex column company-overview-item">
        <div className="flex justify-between p1">
          <span>{`${t(`${tKey}partners`)}`}</span>
          <span>{partners.length}</span>
        </div>
        <div>
          {partners.length ? (
            partners
              .map((partner, index) => (
                <div key={index} className="flex gap-4 my-4">
                  <Tag variant="pastel-light" round text={index + 1} />
                  <span>{partner.restaurant.name}</span>
                  <span>
                    {t(`utils.offerTypes.${partner.offertype.label}`)}
                  </span>
                </div>
              ))
              .slice(0, 6)
          ) : (
            <div className="self-align-center italic">
              {t("utils.errors.noData.title")}
            </div>
          )}
        </div>
        <div className="mt-auto ml-auto">
          <Button
            className="text-light"
            link
            onClick={() => navigate("dashboard/partners")}
          >
            {t(`${tKey}viewAllPartners`)}
            <FontAwesomeIcon className="ml-2" icon={faArrowRightLong} />
          </Button>
        </div>
      </div>
      <div className="w-30 flex column bg-white rounded p-3 company-overview-item">
        <div className="flex justify-between p1">
          <span>{`${t(`${tKey}validationCode`)}`}</span>
          <Button
            variant="primary"
            icon
            onClick={() =>
              copyToClipboard(
                validationCode,
                t("utils.common.copiedToClipboard")
              )
            }
          >
            <FontAwesomeIcon icon={faClipboard} />
          </Button>
        </div>
        <div className="flex justify-between m-4">
          {validationCode.split("").map((char, index) => (
            <span key={index} className="p1 bold bg-pastel-light rounded p-2">
              {char}
            </span>
          ))}
        </div>
        <div>{t(`${tKey}validationCodeDescription`)}</div>
      </div>
      <div className="bg-white rounded p-3 flex w-50 column company-overview-item">
        <div className="flex justify-between p1">
          <span>{`${t(`${tKey}employees`)}`}</span>
          <span>{employees.length}</span>
        </div>
        <div className="flex column justify-between h-100">
          {employees.length ? (
            employees.map((employee, index) => (
              <div key={index} className="flex gap-4 my-4">
                <Tag variant="pastel-light" round text={index + 1} />
                <span>{`${employee.firstname} ${employee.lastname}`}</span>
                <span>{employee.email}</span>
              </div>
            ))
          ) : (
            <div className="mt-4 self-align-center italic">
              {t("utils.errors.noData.title")}
            </div>
          )}
        </div>
        <div className="mt-auto ml-auto">
          <Button
            className="text-light"
            link
            onClick={() => navigate("dashboard/employees")}
          >
            {t(`${tKey}viewAllEmployees`)}
            <FontAwesomeIcon className="ml-2" icon={faArrowRightLong} />
          </Button>
        </div>
      </div>
      <div className="bg-white rounded p-3 flex flex-grow column company-overview-item">
        <div className="flex justify-between p1">
          <span>{`${t(`${tKey}pendingEmployees`)}`}</span>
          <span>{pendingEmployees.length}</span>
        </div>
        <div className="flex justify-between h-100">
          {pendingEmployees.length ? (
            pendingEmployees.map((employee, index) => (
              <div key={index} className="flex gap-4 my-4">
                <Tag variant="pastel-light" round text={index + 1} />
                <span>{`${employee.firstname} ${employee.lastname}`}</span>
                <span>{employee.email}</span>
              </div>
            ))
          ) : (
            <div className="self-align-center italic">
              {t("utils.errors.noData.title")}
            </div>
          )}
        </div>
        <div className="mt-auto ml-auto">
          <Button
            className="text-light"
            link
            onClick={() => navigate("dashboard/employees")}
          >
            {t(`${tKey}viewAllEmployees`)}
            <FontAwesomeIcon className="ml-2" icon={faArrowRightLong} />
          </Button>
        </div>
      </div>
    </div>
      <Modal
        isOpen={domainPromptOpen}
        onClose={dismissDomainPrompt}
        noFooter
        noBgClickable
      >
        <h5 className="mt-0">{t(`${tKey}domainPrompt.title`)}</h5>
        <p>{t(`${tKey}domainPrompt.text`, { domain: suggestedDomain })}</p>
        <div className="modal-footer">
          <Button variant="secondary" onClick={dismissDomainPrompt}>
            {t(`${tKey}domainPrompt.later`)}
          </Button>
          <Button variant="success" onClick={confirmDomainPrompt}>
            {t(`${tKey}domainPrompt.confirm`, { domain: suggestedDomain })}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default CompanyOverview;
